import { useCallback, useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

interface UseRouteTabSettings<T extends string> {
    tabs: readonly T[]
    defaultTab: T
    getRoute: (tab: T) => string
}

export default function useRouteTab<T extends string>({
    tabs,
    defaultTab,
    getRoute,
}: UseRouteTabSettings<T>) {
    const { tab } = useParams()
    const location = useLocation()
    const navigate = useNavigate()

    const routeTab = tab && tabs.includes(tab as T) ? (tab as T) : undefined
    const pathnameTab = tabs.find(item => getRoute(item) === location.pathname)
    const activeKey = routeTab || pathnameTab || defaultTab
    const hasValidTab = Boolean(routeTab || pathnameTab)

    useEffect(() => {
        if (hasValidTab) return

        navigate(`${getRoute(defaultTab)}${location.search}`, { replace: true })
    }, [defaultTab, getRoute, hasValidTab, location.search, navigate])

    const onTabChange = useCallback(
        (key: string) => {
            if (!tabs.includes(key as T)) return

            navigate(`${getRoute(key as T)}${location.search}`)
        },
        [getRoute, location.search, navigate, tabs]
    )

    return { activeKey, onTabChange }
}
